import React, { useState, useEffect } from 'react';
import { getAccessToken } from '../auth/tokenService';
import './UserManagement.css';

const API_BASE = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/admin`
  : '/api/admin';

function getHeaders() {
  const token = getAccessToken();
  return {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  };
}

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Filters
  const [filterUsername, setFilterUsername] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [showUsernameFilter, setShowUsernameFilter] = useState(false);
  const [showRoleFilter, setShowRoleFilter] = useState(false);

  // Add user form
  const [newUsername, setNewUsername] = useState('');
  const [newFullName, setNewFullName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('admin');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/users`, {
        headers: getHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    try {
      const response = await fetch(`${API_BASE}/users`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          username: newUsername,
          full_name: newFullName,
          email: newEmail,
          role: newRole,
        }),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to add user');
      }
      setMessage('Admin added successfully');
      setShowModal(false);
      setNewUsername('');
      setNewFullName('');
      setNewEmail('');
      setNewRole('admin');
      loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRoleChange = async (email, newRole) => {
    try {
      const response = await fetch(`${API_BASE}/users/${encodeURIComponent(email)}/role`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ role: newRole }),
      });
      if (response.ok) {
        setMessage(`Role updated for ${email}`);
        loadUsers();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemoveUser = async (email) => {
    if (!window.confirm(`Remove admin "${email}"?`)) return;
    try {
      const response = await fetch(`${API_BASE}/users/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });
      if (response.ok) {
        setMessage(`Admin "${email}" removed`);
        loadUsers();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="um-page">
      <main className="um-content">
        <div className="um-header-row">
          <h2 className="page-title">Admin Management</h2>
          <button className="btn-add-user" onClick={() => setShowModal(true)}>+ Add Admin</button>
        </div>

        {message && <div className="um-success">{message}</div>}
        {error && <div className="um-error">{error}</div>}

        {/* Users Table */}
        <div className="um-table-wrapper">
          <table className="um-table">
            <thead>
              <tr>
                <th>#</th>
                <th className="th-filterable">
                  <div className="th-filter-wrapper">
                    <span>Username</span>
                    <span className="th-filter-icon" onClick={() => setShowUsernameFilter(!showUsernameFilter)}>▼</span>
                  </div>
                  {showUsernameFilter && (
                    <select
                      className="th-filter-select"
                      value={filterUsername}
                      onChange={e => setFilterUsername(e.target.value)}
                    >
                      <option value="">All</option>
                      {[...new Set(users.map(u => u.username))].filter(Boolean).map(name => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  )}
                </th>
                <th>Full Name</th>
                <th>Email</th>
                <th className="th-filterable">
                  <div className="th-filter-wrapper">
                    <span>Current Role</span>
                    <span className="th-filter-icon" onClick={() => setShowRoleFilter(!showRoleFilter)}>▼</span>
                  </div>
                  {showRoleFilter && (
                    <select
                      className="th-filter-select"
                      value={filterRole}
                      onChange={e => setFilterRole(e.target.value)}
                    >
                      <option value="">All</option>
                      <option value="admin">Admin</option>
                      <option value="superadmin">SuperAdmin</option>
                    </select>
                  )}
                </th>
                <th>Change Role</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="um-loading">Loading...</td></tr>
              ) : users.filter(u => {
                const matchUsername = !filterUsername || (u.username || '') === filterUsername;
                const matchRole = !filterRole || (u.role || '') === filterRole;
                return matchUsername && matchRole;
              }).length === 0 ? (
                <tr><td colSpan="7" className="um-empty">No admins found. Click "+ Add Admin" to create one.</td></tr>
              ) : (
                users.filter(u => {
                  const matchUsername = !filterUsername || (u.username || '') === filterUsername;
                  const matchRole = !filterRole || (u.role || '') === filterRole;
                  return matchUsername && matchRole;
                }).map((u, idx) => (
                  <tr key={u.email}>
                    <td>{idx + 1}</td>
                    <td>{u.username}</td>
                    <td>{u.full_name}</td>
                    <td>{u.email}</td>
                    <td><span className={`role-tag role-tag-${(u.role || '').toLowerCase().replace(' ', '')}`}>{u.role?.toUpperCase()}</span></td>
                    <td>
                      <select
                        value={u.role?.toLowerCase().replace(' ', '')}
                        onChange={(e) => handleRoleChange(u.email, e.target.value)}
                        className="um-role-select"
                      >
                        <option value="superadmin">SuperAdmin</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td>
                      <button className="btn-remove" onClick={() => handleRemoveUser(u.email)}>Remove</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Add Admin Modal */}
        {showModal && (
          <div className="um-modal-overlay" onClick={() => setShowModal(false)}>
            <div className="um-modal" onClick={e => e.stopPropagation()}>
              <div className="um-modal-header">
                <h3>Add New Admin</h3>
                <button className="um-modal-close" onClick={() => setShowModal(false)}>✕</button>
              </div>
              <form onSubmit={handleAddUser} className="um-modal-form">
                <div className="form-group">
                  <label>Username</label>
                  <input
                    type="text"
                    value={newUsername}
                    onChange={e => setNewUsername(e.target.value)}
                    placeholder=""
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    value={newFullName}
                    onChange={e => setNewFullName(e.target.value)}
                    placeholder=""
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={e => setNewEmail(e.target.value)}
                    placeholder=""
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Role</label>
                  <select value={newRole} onChange={e => setNewRole(e.target.value)}>
                    <option value="admin">Admin</option>
                    <option value="superadmin">SuperAdmin</option>
                  </select>
                </div>
                <div className="um-modal-actions">
                  <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="btn-add-submit">Add Admin</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
